import { useState } from 'react'
import { Flex } from '@wiggui/ds/Flex'
import { Transition } from '@wiggui/ds/Transition'
import { MdOutlineChevronRight } from 'react-icons/md'
import { composeClasses } from '../../../../utils/src/strings'
import type { OptionMenu } from '../hooks/useGetMenuOptions'
import HardLink from '../../../components/hard-link/HardLink'
import './SidebarOption.css'

interface SidebarOptionProps {
  option: OptionMenu
  expandedOption: string
  setExpandedOption: (value: string) => void
  setShowSideMenuOptions: (value: boolean) => void
}

export const SidebarOption = ({
  option,
  expandedOption,
  setExpandedOption,
  setShowSideMenuOptions
}: SidebarOptionProps) => {
  const isOptionActive = option?.id === expandedOption

  const [isOptionExpanded, setIsOptionExpanded] = useState(true)

  const hasSubOptions = Boolean(
    option?.subOptions && option?.subOptions?.length > 0
  )

  const showSubOptions = Boolean(hasSubOptions && isOptionExpanded)

  return (
    <li
      className={composeClasses(
        hasSubOptions && 'flex flex-col items-baseline',
        isOptionActive && 'bg-surface-gray-10',
        showSubOptions && 'pb-3',
        'list-none transition-colors duration-300 ease-in-out mx-3'
      )}
    >
      <HardLink
        href={option?.url || ''}
        className={composeClasses(
          option?.isActive && 'bg-surface-gray-10',
          !showSubOptions && 'border-b border-surface-strokes',
          'flex flex-row items-center justify-between gap-3 w-full py-2 cursor-pointer active:bg-surface-gray-10 group transition-colors duration-300 ease-in-out px-3'
        )}
      >
        <Flex dir="row" items="center" justify="start" gap="gap-2" wFull>
          <span
            className={composeClasses(
              'text-primary group-active:font-semibold',
              option?.isActive && 'font-semibold'
            )}
          >
            {option?.icon}
          </span>
          <p
            className={composeClasses(
              option?.isActive
                ? 'text-body-bold-m'
                : 'text-body-m group-active:text-body-bold-m',
              'text-primary whitespace-nowrap text-ellipsis overflow-hidden w-full text-left'
            )}
          >
            {option?.name}
          </p>
        </Flex>
        <Transition
          duration={300}
          show={hasSubOptions}
          className="w-full -my-6 min-w-[36px] min-h-[36px] max-w-[36px] max-h-[36px]"
        >
          <div
            className="rounded-full min-w-[36px] min-h-[36px] max-w-[36px] max-h-[36px] flex items-center justify-center active:bg-surface-gray-10"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              setExpandedOption(
                isOptionActive || isOptionExpanded ? '' : option?.id
              )
              setIsOptionExpanded(!isOptionExpanded)
            }}
          >
            <MdOutlineChevronRight
              className={composeClasses(
                'min-w-[18px] min-h-[18px] max-w-[18px] max-h-[18px] chevron-icon',
                showSubOptions && isOptionExpanded
                  ? 'chevron-icon--expanded'
                  : ''
              )}
            />
          </div>
        </Transition>
      </HardLink>
      <div
        className={composeClasses(
          'suboptions-container w-full',
          showSubOptions && isOptionExpanded
            ? 'suboptions-container--expanded'
            : 'suboptions-container--collapsed'
        )}
      >
        <ul className="flex flex-col w-full">
          {option?.subOptions?.map((subOption) => {
            return (
              <li key={subOption.id}>
                <HardLink
                  href={subOption?.url || ''}
                  onClick={() => {
                    setExpandedOption('')
                    setShowSideMenuOptions(false)
                    setIsOptionExpanded(false)
                  }}
                  className={composeClasses(
                    'flex flex-row items-center justify-start gap-3 w-full px-[10px] py-[6px] active:bg-surface-gray-10 group transition-colors duration-300 ease-in-out'
                  )}
                >
                  <div className="h-[18px] w-[18px]" />
                  <p
                    className={composeClasses(
                      subOption?.isActive
                        ? 'font-bold pointer-events-none'
                        : 'group-active:font-bold',
                      'text-primary text-body-s whitespace-nowrap text-ellipsis overflow-hidden w-full text-left max-w-[110px]'
                    )}
                  >
                    {subOption?.name}
                  </p>
                </HardLink>
              </li>
            )
          })}
        </ul>
      </div>
    </li>
  )
}
